import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { Currency } from '@freelancer/datastore/collections';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { HeadingType } from '@freelancer/ui/heading';
import { LinkColor } from '@freelancer/ui/link';
import { Margin } from '@freelancer/ui/margin';
import { FontColor, FontType, TextAlign, TextSize } from '@freelancer/ui/text';
import { ErrorCodeApi } from 'api-typings/errors/errors';
import { ProjectUpgradeFeeTaxTypeApi } from 'api-typings/projects/projects';
import * as Rx from 'rxjs';
import { FormSubmitApiError } from '../../form.component';
import { JobType } from '../../job-type/job-type.component';

@Component({
  selector: 'app-submit',
  template: `
    <ng-container
      *ngIf="
        selectedUpgrades?.length > 0 &&
        jobType !== JobType.CONTEST &&
        (error$ | async)?.errorCode !== 'CART_PAYMENT_ERROR' &&
        totalFee > 0
      "
    >
      <fl-banner-alert
        i18n="
           Post project banner alert informing that user can proceed even if
          upgrades are not yet paid
        "
        [closeable]="false"
        [flMarginBottom]="Margin.LARGE"
        [type]="BannerAlertType.INFO"
      >
        Your project will be sent live immediately, but you will not be charged
        yet.
      </fl-banner-alert>
    </ng-container>
    <ng-container *ngIf="error$ | async as error" [ngSwitch]="error?.errorCode">
      <fl-banner-alert
        *ngSwitchCase="'BLOCKED_CALIFORNIAN_ERROR'"
        i18n="
           Error message for Californian users trying to post with unsupported
          currency
        "
        [closeable]="false"
        [flMarginBottom]="Margin.SMALL"
        [type]="BannerAlertType.ERROR"
      >
        Please select a supported currency for Californian users and try again.
      </fl-banner-alert>
      <fl-banner-alert
        *ngSwitchCase="'CART_PAYMENT_ERROR'"
        i18n-bannerTitle="Cart payment error banner title"
        bannerTitle="Payment processing failed"
        [bannerAlertButton]="
          jobType === JobType.PROJECT || jobType === JobType.LOCAL
            ? { text: 'Go to project', link: '/projects/' + error.contextId }
            : { text: 'Go to contest', link: '/contest/' + error.contextId }
        "
        [closeable]="false"
        [flMarginBottom]="Margin.SMALL"
        [type]="BannerAlertType.WARNING"
      >
        <fl-text
          *ngIf="jobType === JobType.CONTEST"
          i18n="
             Error message when an error occured while processing cart items
          "
        >
          We encountered a problem processing your payment. Your contest details
          have been saved and you may send it live by completing payment any
          time.
        </fl-text>
        <fl-text
          *ngIf="jobType === JobType.PROJECT || jobType === JobType.LOCAL"
          i18n="
             Error message when an error occured while processing cart items
          "
        >
          Your project has been posted successfully, but we encountered a
          problem processing payment for your upgrades. You may still add
          upgrades on your project's page.
        </fl-text>
      </fl-banner-alert>
      <ng-container *ngSwitchDefault>
        <ng-container
          *ngIf="jobType === JobType.CONTEST"
          [ngSwitch]="error.errorCode"
        >
          <fl-banner-alert
            *ngSwitchCase="'CONTEST_USER_RESTRICTED'"
            bannerTitle="You are currently restricted from posting contests"
            i18n-bannerTitle="
               Post contest error banner title for restricted accounts
            "
            [closeable]="false"
            [flMarginBottom]="Margin.SMALL"
            [type]="BannerAlertType.ERROR"
          >
            <fl-text i18n="Error message for restricted users">
              A restriction on your account has been placed as a penalty. You
              may not post nor award new jobs until the restriction has been
              lifted. Please do not hesitate to
              <fl-link flTrackingLabel="GoToSupport" [link]="'/support/'">
                contact support
              </fl-link>
              for further questions.
            </fl-text>
          </fl-banner-alert>
          <fl-banner-alert
            *ngSwitchDefault
            bannerTitle="Failed to create contest"
            i18n-bannerTitle="Post contest error banner title"
            [closeable]="false"
            [flMarginBottom]="Margin.SMALL"
            [type]="BannerAlertType.ERROR"
          >
            <fl-text
              *ngIf="error?.requestId; else noRequestId"
              i18n="
                 Generic error message with request ID when an error occured
                while posting a contest
              "
            >
              We encountered an error while posting your contest. Please refresh
              the page to try again or contact support@freelancer.com with this
              request ID:
              <fl-text class="SubmitErrorRequestId" [fontType]="FontType.SPAN">
                {{ error.requestId }}.
              </fl-text>
            </fl-text>
            <ng-template #noRequestId>
              <fl-text
                i18n="
                   Generic error message when an error occured while posting a
                  contest
                "
              >
                We encountered an error while posting your contest. Please
                refresh the page to try again or contact support@freelancer.com
                if the issue persists.
              </fl-text>
            </ng-template>
          </fl-banner-alert>
        </ng-container>
        <ng-container
          *ngIf="jobType === JobType.PROJECT || jobType === JobType.LOCAL"
          [ngSwitch]="error.errorCode"
        >
          <fl-banner-alert
            *ngSwitchCase="'PROJECT_USER_RESTRICTED'"
            bannerTitle="You are currently restricted from posting projects"
            i18n-bannerTitle="
               Post project error banner title for restricted accounts
            "
            [closeable]="false"
            [flMarginBottom]="Margin.SMALL"
            [type]="BannerAlertType.ERROR"
          >
            <fl-text
              i18n="
                 Error message when user is restricted from posting a project
              "
            >
              A restriction on your account has been placed as a penalty. You
              may not post nor award new jobs until the restriction has been
              lifted. Please do not hesitate to
              <fl-link flTrackingLabel="GoToSupport" [link]="'/contact'">
                contact support
              </fl-link>
              for further questions.
            </fl-text>
          </fl-banner-alert>
          <fl-banner-alert
            *ngSwitchCase="'REQUIRED_VALIDATION_FAILED'"
            bannerTitle="Equipment processing failed"
            i18n-bannerTitle="Creating equipment error banner title"
            [closeable]="false"
            [flMarginBottom]="Margin.SMALL"
            [type]="BannerAlertType.ERROR"
          >
            <fl-text
              i18n="
                 Error message when an error occured while creating equipment
              "
            >
              We encountered a problem adding your equipment. Please refresh the
              page to try again or contact support@freelancer.com if the issue
              persists.
            </fl-text>
          </fl-banner-alert>
          <fl-banner-alert
            *ngSwitchDefault
            bannerTitle="Failed to create project"
            i18n-bannerTitle="
               Post project error banner title for non-restricted accounts
            "
            [closeable]="false"
            [flMarginBottom]="Margin.SMALL"
            [type]="BannerAlertType.ERROR"
          >
            <ng-container [ngSwitch]="error.errorCode">
              <fl-text
                *ngSwitchCase="'PAYMENT_VERIFICATION_REQUIRED'"
                i18n="
                   Error message when payment verification is required before
                  posting a project
                "
              >
                To proceed with posting your project, you must
                <fl-link
                  flTrackingLabel="GoToPaymentVerification"
                  [link]="'/payments/verify.php'"
                >
                  add a verified payment method.
                </fl-link>
                Come back to this page when you're done, we'll save all your
                project details so that you can easily post your project after
                payment verification.
              </fl-text>
              <ng-container *ngSwitchDefault>
                <fl-text
                  *ngIf="error?.requestId; else noRequestId"
                  i18n="
                     Generic error message with request ID when an error occured
                    while posting a project
                  "
                >
                  We encountered an error while posting your project. Please
                  refresh the page to try again or contact
                  support@freelancer.com with this request ID:
                  <fl-text
                    class="SubmitErrorRequestId"
                    [fontType]="FontType.SPAN"
                  >
                    {{ error.requestId }}.
                  </fl-text>
                </fl-text>
                <ng-template #noRequestId>
                  <fl-text
                    i18n="
                       Generic error message when an error occured while posting
                      a project
                    "
                  >
                    We encountered an error while posting your project. Please
                    refresh the page to try again or contact
                    support@freelancer.com if the issue persists.
                  </fl-text>
                </ng-template>
              </ng-container>
            </ng-container>
          </fl-banner-alert>
        </ng-container>
      </ng-container>
    </ng-container>
    <fl-bit
      *ngIf="(error$ | async)?.errorCode !== 'CART_PAYMENT_ERROR'"
      class="SectionCta"
      [flMarginBottom]="Margin.SMALL"
    >
      <fl-bit *ngIf="totalFee > 0" [flMarginRight]="Margin.MID">
        <fl-heading
          class="TotalFee"
          i18n="
             Total price to be paid for the job based on the data entered in the
            form
          "
          [headingType]="HeadingType.H3"
          [size]="TextSize.MID"
        >
          Total: {{ totalFee | flCurrency: selectedCurrency.code }}
        </fl-heading>
        <ng-container [ngSwitch]="taxType">
          <fl-text
            *ngSwitchCase="ProjectUpgradeFeeTaxTypeApi.GST"
            i18n="
               Indicator that GST is included in total computed price because
              the country of the user has it
            "
            [color]="FontColor.MID"
            [size]="TextSize.XSMALL"
            [textAlign]="TextAlign.RIGHT"
          >
            Including GST
          </fl-text>
          <fl-text
            *ngSwitchCase="ProjectUpgradeFeeTaxTypeApi.VAT"
            i18n="
               Indicator that VAT is included in total computed price because
              the country of the user has it
            "
            [color]="FontColor.MID"
            [size]="TextSize.XSMALL"
            [textAlign]="TextAlign.RIGHT"
          >
            Including VAT
          </fl-text>
        </ng-container>
      </fl-bit>
      <fl-button
        *ngIf="jobType === JobType.PROJECT || jobType === JobType.LOCAL"
        i18n="Post Job Page Post Project Button"
        flTrackingLabel="PostProject"
        [busy]="isSubmitting"
        [color]="ButtonColor.PRIMARY"
        [size]="ButtonSize.LARGE"
        (click)="onSubmit.emit()"
      >
        Yes, post my project
      </fl-button>
      <fl-button
        *ngIf="jobType === JobType.CONTEST"
        i18n="Post Job Page Post Contest Button"
        flTrackingLabel="PostContest"
        [busy]="isSubmitting"
        [color]="ButtonColor.PRIMARY"
        [size]="ButtonSize.LARGE"
        (click)="onSubmit.emit()"
      >
        Get entries now
      </fl-button>
    </fl-bit>
    <fl-hr [flMarginBottom]="Margin.SMALL"></fl-hr>
    <fl-text
      flTrackingSection="PJPFooter"
      i18n="Post project page footer terms"
      [color]="FontColor.MID"
      [size]="TextSize.XXSMALL"
    >
      By clicking
      <ng-container *ngIf="jobType === JobType.CONTEST; else postProject">
        'Get entries now'</ng-container
      >
      <ng-template #postProject>'Yes, post my project'</ng-template>, you agree
      to the
      <fl-link
        flTrackingLabel="TermsAndConditions"
        [color]="LinkColor.DARK"
        [link]="'/about/terms'"
        [newTab]="true"
        [size]="TextSize.XXSMALL"
      >
        Terms & Conditions
      </fl-link>
      and
      <fl-link
        flTrackingLabel="PrivacyPolicy"
        [color]="LinkColor.DARK"
        [link]="'/about/privacy'"
        [newTab]="true"
        [size]="TextSize.XXSMALL"
      >
        Privacy Policy
      </fl-link>
    </fl-text>
    <fl-text
      i18n="Post project page footer copyright"
      [size]="TextSize.XXSMALL"
      [color]="FontColor.MID"
    >
      Copyright &copy; {{ currentYear }} Freelancer Technology Pty Limited (ACN
      142 189 759)
    </fl-text>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./submit.component.scss'],
})
export class SubmitComponent {
  BannerAlertType = BannerAlertType;
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  ErrorCodeApi = ErrorCodeApi;
  FontColor = FontColor;
  FontType = FontType;
  HeadingType = HeadingType;
  JobType = JobType;
  LinkColor = LinkColor;
  Margin = Margin;
  ProjectUpgradeFeeTaxTypeApi = ProjectUpgradeFeeTaxTypeApi;
  TextAlign = TextAlign;
  TextSize = TextSize;

  currentYear: number = new Date().getFullYear();

  @Input() error$: Rx.Observable<FormSubmitApiError | null>;
  @Input() jobType: JobType;
  @Input() isSubmitting: boolean;
  @Input() selectedCurrency: Currency;
  @Input() selectedUpgrades: ReadonlyArray<string>;
  @Input() taxType: ProjectUpgradeFeeTaxTypeApi;
  @Input() totalFee: number;

  @Output() onSubmit = new EventEmitter<void>();
}
